import { cn } from "@/lib/cn";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  type MotionValue,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { HOTEL_FLOORS, HOTEL_OUTLINE, TOP_FLOOR_ROOM_IDS } from "./hotel";
import {
  buildIsometricBlock,
  project,
  toPoints,
  useSvgId,
  type Vertex,
} from "./isometric";
import { RoomCards } from "./RoomCards";
import { ROOM_DATA } from "./roomData";

const WALL_HEIGHT = 40;
const SLAB_HEIGHT = 8;
const FLOOR_SPACING = WALL_HEIGHT + SLAB_HEIGHT;
// Matches the configurator, so the ground shadow sits the same distance below.
const SHADOW_DROP = 40;
const STILL_ANGLE = -35;
const SCROLL_ANGLES = [STILL_ANGLE - 30, STILL_ANGLE + 30];
const DRAG_DEGREES_PER_PX = 0.4;
const CLICK_SLOP_PX = 4;
const SPIN_DEGREES_PER_SECOND = 8;
const TOUR_INTERVAL_MS = 5000;
const TOUR_RESUME_MS = 8000;
const SELECT_FADE_MS = 500;
const HOVER_FADE_MS = 200;
const INITIAL_ROOM_ID = "2-03";
// Vacant rooms use the configurator's palette; the selected room follows the
// website illustration: cyan-to-pink faces and orange-to-pink edges.
const VACANT = {
  top: "#f3f4f6",
  wall: [229, 231, 235],
  stroke: [223, 226, 231],
};
const HOVER_TOP = "#fdf4f1";
// Translucent like the configurator: walls behind show through and darken
// the ones in front, which gives the building its depth.
const ROOM_OPACITY = 0.8;
const FLOOR = { plate: "#f5f5f5", stroke: "#c2c2c2" };
const ROOM_TRANSITION =
  "transition-[fill,stroke,fill-opacity,opacity] duration-500 ease-in-out motion-reduce:transition-none";

export function BuildingDemo({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref);
  const [selectedId, setSelectedId] = useState(INITIAL_ROOM_ID);
  const [explored, setExplored] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragAngle = useMotionValue(0);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    x: number;
    moved: boolean;
  } | null>(null);
  const dragged = useRef(false);
  // Only the pointer that started the drag moves or ends it, so a second
  // finger can't take over and make the building jump.
  const endDrag = (event: PointerEvent) => {
    if (drag.current?.pointerId !== event.pointerId) {
      return;
    }
    dragged.current = drag.current.moved;
    drag.current = null;
    setDragging(false);
  };
  // A press that never moved past the click slop has no pointer capture, so
  // its release can land on another element; the window always sees it.
  useEffect(() => {
    if (!dragging) {
      return;
    }
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
    // endDrag only touches refs and a state setter, so the first one stays valid.
  }, [dragging]);
  const select = (roomId: string) => {
    setSelectedId(roomId);
    setExplored(true);
  };
  const autoplay = inView && !reduceMotion && !hovered && !focused && !dragging;

  useEffect(() => {
    if (!autoplay || explored) {
      return;
    }
    const timer = setInterval(
      () =>
        setSelectedId(
          (id) =>
            TOP_FLOOR_ROOM_IDS[
              (TOP_FLOOR_ROOM_IDS.indexOf(id) + 1) % TOP_FLOOR_ROOM_IDS.length
            ],
        ),
      TOUR_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [autoplay, explored]);

  // The tour hands control back once the visitor has left it alone for a while.
  useEffect(() => {
    if (!autoplay || !explored) {
      return;
    }
    const timer = setTimeout(() => setExplored(false), TOUR_RESUME_MS);
    return () => clearTimeout(timer);
  }, [autoplay, explored, selectedId]);

  const svgProps = { selectedId, onSelect: select, onHoverRoom: setHovered };

  return (
    <div
      className={cn("relative flex flex-col gap-6 pb-6 xl:pb-16", className)}
      // Keyboard focus pauses autoplay; a mouse click leaves focus behind on
      // the room, which would otherwise pause it until the next click away.
      onFocus={(event) => setFocused(event.target.matches(":focus-visible"))}
      onBlur={(event) =>
        setFocused(event.currentTarget.contains(event.relatedTarget))
      }
    >
      {/* pan-y leaves vertical swipes to the page, so a phone still scrolls.
          Capture starts only past the click slop, so a tap still reaches the room. */}
      <div
        ref={ref}
        className="w-full cursor-grab touch-pan-y touch-pinch-zoom select-none active:cursor-grabbing xl:mx-auto xl:w-1/2"
        onPointerDown={(event) => {
          if (event.button !== 0 || drag.current) {
            return;
          }
          dragged.current = false;
          setDragging(true);
          drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            x: event.clientX,
            moved: false,
          };
        }}
        onPointerMove={(event) => {
          const current = drag.current;
          if (current?.pointerId !== event.pointerId) {
            return;
          }
          // The pointerup can land outside the element (or in a context
          // menu); a move with no button held means the press is over.
          if (event.buttons === 0) {
            endDrag(event.nativeEvent);
            return;
          }
          if (!current.moved) {
            if (Math.abs(event.clientX - current.startX) < CLICK_SLOP_PX) {
              return;
            }
            current.moved = true;
            event.currentTarget.setPointerCapture(event.pointerId);
          }
          const dx = event.clientX - current.x;
          current.x = event.clientX;
          dragAngle.set(dragAngle.get() - dx * DRAG_DEGREES_PER_PX);
        }}
        onClickCapture={(event) => {
          if (dragged.current) {
            event.stopPropagation();
          }
        }}
      >
        <TurningBuildingSvg
          target={ref}
          dragAngle={dragAngle}
          moving={!reduceMotion}
          spinning={autoplay}
          {...svgProps}
        />
      </div>
      <RoomCards room={ROOM_DATA[selectedId]} announce={explored} />
    </div>
  );
}

type RoomProps = {
  selectedId: string;
  onSelect: (roomId: string) => void;
  // Autoplay pauses only while a room is under the mouse, not anywhere over
  // the demo, so resting the cursor on the cards or empty space keeps it going.
  onHoverRoom: (hovering: boolean) => void;
};

// Scroll, spin and drag all feed one motion value, so rotating re-renders only
// the SVG, never the demo around it or the cards.
function TurningBuildingSvg({
  target,
  dragAngle,
  moving,
  spinning,
  ...roomProps
}: RoomProps & {
  target: RefObject<HTMLDivElement>;
  dragAngle: MotionValue<number>;
  moving: boolean;
  spinning: boolean;
}) {
  const { scrollYProgress } = useScroll({
    target,
    offset: ["start end", "end start"],
  });
  const scrollAngle = useTransform(scrollYProgress, [0, 1], SCROLL_ANGLES);
  const spin = useMotionValue(0);
  const angle = useTransform(
    () =>
      (moving ? scrollAngle.get() + spin.get() : STILL_ANGLE) + dragAngle.get(),
  );
  useEffect(() => {
    if (!spinning) {
      return;
    }
    // A full turn looks identical, so looping back to the start is seamless.
    const controls = animate(spin, spin.get() + 360, {
      duration: 360 / SPIN_DEGREES_PER_SECOND,
      ease: "linear",
      repeat: Infinity,
    });
    return () => controls.stop();
  }, [spinning, spin]);
  const [degrees, setDegrees] = useState(() => angle.get());
  // Quarter-degree steps: smooth at the spin speed, and sub-pixel scroll
  // movement still skips a re-render.
  useMotionValueEvent(angle, "change", (value) =>
    setDegrees(Math.round(value * 4) / 4),
  );
  return (
    <BuildingSvg
      degrees={degrees}
      label={
        moving
          ? "Hotel rooms. Pick one to see its status; scroll or drag to turn the building."
          : "Hotel rooms. Pick one to see its status, or drag to turn the building."
      }
      {...roomProps}
    />
  );
}

function BuildingSvg({
  degrees,
  label,
  selectedId,
  onSelect,
  onHoverRoom,
}: RoomProps & { degrees: number; label: string }) {
  const gradientId = useSvgId("active-wall");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const hover = (roomId: string | null) => {
    setHoveredId(roomId);
    onHoverRoom(roomId !== null);
  };
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const reduceMotion = useReducedMotion();
  const fadingSelectedId = useLingering(selectedId, SELECT_FADE_MS);
  const fadingHoveredId = useLingering(hoveredId, HOVER_FADE_MS);
  const { floors, shadow } = useMemo(() => {
    const theta = (degrees * Math.PI) / 180;
    return { floors: buildFloors(theta), shadow: groundShadow(theta) };
  }, [degrees]);
  // Only rooms showing (or fading out of) an outline get one: building an
  // outline and its occlusion mask for every room each frame is wasted work.
  const outlinedKey = [
    ...new Set([
      selectedId,
      fadingSelectedId,
      hoveredId,
      fadingHoveredId,
      focusedId,
    ]),
  ]
    .filter((id) => id !== null)
    .join(" ");
  const outlines = useMemo(
    () =>
      buildOutlines(floors[floors.length - 1].rooms, outlinedKey.split(" ")),
    [floors, outlinedKey],
  );
  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    const offset = ARROW_OFFSETS[event.key];
    const roomId = offset
      ? TOP_FLOOR_ROOM_IDS[
          (TOP_FLOOR_ROOM_IDS.indexOf(selectedId) +
            offset +
            TOP_FLOOR_ROOM_IDS.length) %
            TOP_FLOOR_ROOM_IDS.length
        ]
      : (event.key === "Enter" || event.key === " ") && roomIdOf(event.target);
    if (!roomId) {
      return;
    }
    event.preventDefault();
    onSelect(roomId);
    event.currentTarget
      .querySelector<SVGElement>(`[role="radio"][data-room="${roomId}"]`)
      ?.focus();
  };

  return (
    <svg
      viewBox="-20 -36 680 400"
      overflow="visible"
      role="radiogroup"
      aria-label={label}
      className="block h-auto w-full"
      onClick={(event) => {
        const roomId = roomIdOf(event.target);
        if (roomId) {
          onSelect(roomId);
        }
      }}
      onKeyDown={onKeyDown}
      onPointerOver={(event) => {
        if (event.pointerType === "mouse") {
          hover(roomIdOf(event.target) ?? null);
        }
      }}
      onPointerLeave={() => hover(null)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0.31" y1="0.12" x2="0.5" y2="1">
          <stop offset="0" stopColor="#d8fafb" />
          <stop offset="1" stopColor="#ffdeeb" />
        </linearGradient>
        <linearGradient id={`${gradientId}-edge`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#df461c" />
          <stop offset="1" stopColor="#f48fb1" />
        </linearGradient>
        <filter
          id={`${gradientId}-shadow`}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur stdDeviation="24" />
        </filter>
      </defs>
      <polygon
        points={shadow}
        fill="black"
        fillOpacity={0.1}
        filter={`url(#${gradientId}-shadow)`}
      />
      {floors.map((floor, floorIndex) => {
        const topFloor = floorIndex === floors.length - 1;
        return (
          <g key={floor.id} strokeLinejoin="round">
            {floor.slabWalls.map((wall) => (
              <polygon
                key={wall.id}
                vectorEffect="non-scaling-stroke"
                points={toPoints(wall.points)}
                fill="white"
                stroke={FLOOR.stroke}
              />
            ))}
            <polygon
              vectorEffect="non-scaling-stroke"
              points={floor.plate}
              fill={FLOOR.plate}
              stroke={FLOOR.stroke}
            />
            {floor.walls.map(({ key, roomId, wall }) => {
              const clickable = topFloor;
              return (
                <g
                  key={key}
                  data-room={clickable ? roomId : undefined}
                  className={clickable ? "cursor-pointer" : undefined}
                >
                  <polygon
                    vectorEffect="non-scaling-stroke"
                    points={toPoints(wall.points)}
                    fillOpacity={ROOM_OPACITY}
                    fill={shade(VACANT.wall, wall.rightness)}
                    stroke={shade(VACANT.stroke, wall.rightness)}
                  />
                  {/* A gradient can't transition from a flat fill, so the
                    selected wall is a layer that fades in over the vacant one. */}
                  {clickable && (
                    <polygon
                      vectorEffect="non-scaling-stroke"
                      points={toPoints(wall.points)}
                      fill={`url(#${gradientId})`}
                      fillOpacity={ROOM_OPACITY}
                      stroke="none"
                      className={cn(
                        ROOM_TRANSITION,
                        roomId === selectedId ? "opacity-100" : "opacity-0",
                      )}
                    />
                  )}
                </g>
              );
            })}
            {floor.tops.map(({ id, points }) => {
              const selected = id === selectedId;
              const top = (
                <polygon
                  vectorEffect="non-scaling-stroke"
                  points={points}
                  stroke={shade(VACANT.stroke, 0)}
                  className={ROOM_TRANSITION}
                  fillOpacity={ROOM_OPACITY}
                  style={{
                    fill:
                      id === hoveredId && !selected ? HOVER_TOP : VACANT.top,
                  }}
                />
              );
              if (!topFloor) {
                return <Fragment key={id}>{top}</Fragment>;
              }
              return (
                <g
                  key={id}
                  data-room={id}
                  role="radio"
                  aria-checked={selected}
                  aria-label={ROOM_DATA[id].name}
                  tabIndex={selected ? 0 : -1}
                  className="cursor-pointer outline-none"
                  onFocus={(event) =>
                    setFocusedId(
                      event.currentTarget.matches(":focus-visible") ? id : null,
                    )
                  }
                  onBlur={() => setFocusedId(null)}
                >
                  {top}
                  <polygon
                    vectorEffect="non-scaling-stroke"
                    points={points}
                    fill={`url(#${gradientId})`}
                    fillOpacity={ROOM_OPACITY}
                    stroke="none"
                    className={cn(
                      ROOM_TRANSITION,
                      selected ? "opacity-100" : "opacity-0",
                    )}
                  />
                </g>
              );
            })}
          </g>
        );
      })}
      {/* Outlines are drawn last so neighbouring rooms, painted later in depth
          order, can't cover them; only the edges facing the viewer. */}
      <g fill="none" pointerEvents="none">
        {outlines.map(({ id, top, walls, occluders }) => {
          const maskId = `${gradientId}-mask-${id}`;
          const edges = [...walls, top].map((points) => (
            <polygon
              key={points}
              vectorEffect="non-scaling-stroke"
              points={points}
            />
          ));
          const highlight =
            id === selectedId
              ? 0
              : id === focusedId
                ? 1
                : id === hoveredId
                  ? 0.6
                  : 0;
          return (
            <g
              key={id}
              mask={occluders.length > 0 ? `url(#${maskId})` : undefined}
            >
              {occluders.length > 0 && (
                <mask
                  id={maskId}
                  maskUnits="userSpaceOnUse"
                  x="-200"
                  y="-200"
                  width="1100"
                  height="900"
                >
                  <rect
                    x="-200"
                    y="-200"
                    width="1100"
                    height="900"
                    fill="white"
                  />
                  {occluders.map((points) => (
                    <polygon key={points} points={points} fill="black" />
                  ))}
                </mask>
              )}
              <motion.g
                stroke={`url(#${gradientId}-edge)`}
                initial={{ opacity: 0 }}
                animate={{ opacity: id === selectedId ? 1 : 0 }}
                transition={{
                  duration: reduceMotion ? 0 : SELECT_FADE_MS / 1000,
                }}
              >
                {edges}
              </motion.g>
              <motion.g
                strokeWidth={1.5}
                className="stroke-accent"
                initial={{ opacity: 0 }}
                animate={{ opacity: highlight }}
                transition={{
                  duration: reduceMotion ? 0 : HOVER_FADE_MS / 1000,
                }}
              >
                {edges}
              </motion.g>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

const ARROW_OFFSETS: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

function roomIdOf(target: EventTarget) {
  return target instanceof Element
    ? target.closest("[data-room]")?.getAttribute("data-room")
    : undefined;
}

function groundShadow(theta: number) {
  const offsetY = (HOTEL_FLOORS.length - 1) * FLOOR_SPACING + SHADOW_DROP;
  return toPoints(
    HOTEL_OUTLINE.map((point): Vertex => {
      const [x, y] = project(point, theta);
      return [x, y + offsetY];
    }),
  );
}

function buildFloors(theta: number) {
  const place = (outline: Vertex[], offsetY: number) =>
    outline.map((point): Vertex => {
      const [x, y] = project(point, theta);
      return [x, y + offsetY];
    });

  return HOTEL_FLOORS.map((floor, index) => {
    const offsetY = (HOTEL_FLOORS.length - 1 - index) * FLOOR_SPACING;
    const rooms = floor.rooms.map((room) => ({
      id: room.id,
      block: buildIsometricBlock(place(room.outline, offsetY), WALL_HEIGHT),
    }));
    return {
      id: floor.id,
      slabWalls: buildIsometricBlock(
        place(HOTEL_OUTLINE, offsetY + SLAB_HEIGHT),
        SLAB_HEIGHT,
      ).walls,
      plate: toPoints(place(HOTEL_OUTLINE, offsetY)),
      walls: rooms
        .flatMap(({ id, block }) =>
          block.walls.map((wall) => ({
            key: `${id}-${wall.id}`,
            roomId: id,
            wall,
          })),
        )
        .sort((a, b) => a.wall.depthY - b.wall.depthY),
      tops: rooms.map(({ id, block }) => ({
        id,
        points: toPoints(block.topFace),
      })),
      rooms,
    };
  });
}

type ProjectedRoom = ReturnType<typeof buildFloors>[number]["rooms"][number];

function buildOutlines(rooms: ProjectedRoom[], ids: string[]) {
  return rooms
    .filter(({ id }) => ids.includes(id))
    .map(({ id, block }) => {
      const edges = ROOM_EDGES.get(id);
      return {
        id,
        top: toPoints(block.topFace),
        walls: frontWalls(block.walls)
          .filter((wall) => edges?.open.has(wall.id))
          .map((wall) => toPoints(wall.points)),
        // Rooms closer to the viewer that don't share a wall can hide part of
        // this one, so its outline is masked by their shapes.
        occluders: rooms
          .filter(
            (other) =>
              other.id !== id &&
              other.block.depthY > block.depthY &&
              !edges?.neighbors.has(other.id),
          )
          .flatMap((other) => [
            toPoints(other.block.topFace),
            ...other.block.walls.map((wall) => toPoints(wall.points)),
          ]),
      };
    });
}

// Keeps the id that was just replaced for `ms`, so its outline can fade out.
function useLingering(id: string | null, ms: number) {
  const [lingering, setLingering] = useState<string | null>(null);
  const previous = useRef(id);
  useEffect(() => {
    if (previous.current === id) {
      return;
    }
    setLingering(previous.current);
    previous.current = id;
    const timer = setTimeout(() => setLingering(null), ms);
    return () => clearTimeout(timer);
  }, [id, ms]);
  return lingering;
}

// A wall shared with a neighbouring room sits behind that room, so the selection
// outline leaves it out rather than drawing it through the neighbour.
const ROOM_EDGES = roomEdges(HOTEL_FLOORS[HOTEL_FLOORS.length - 1].rooms);

function roomEdges(rooms: { id: string; outline: Vertex[] }[]) {
  const PROBE = 0.01;
  return new Map(
    rooms.map((room) => {
      const neighbors = new Set<string>();
      const open = room.outline.flatMap(([x, y], i) => {
        const [nextX, nextY] = room.outline[(i + 1) % room.outline.length];
        const length = Math.hypot(nextX - x, nextY - y) || 1;
        const [nx, ny] = [
          ((nextY - y) / length) * PROBE,
          ((x - nextX) / length) * PROBE,
        ];
        const [mx, my] = [(x + nextX) / 2, (y + nextY) / 2];
        const probes: Vertex[] = [
          [mx + nx, my + ny],
          [mx - nx, my - ny],
        ];
        const touching = rooms.filter(
          (other) =>
            other.id !== room.id &&
            probes.some((probe) => contains(other.outline, probe)),
        );
        touching.forEach((other) => neighbors.add(other.id));
        return touching.length > 0 ? [] : [i];
      });
      return [room.id, { open: new Set(open), neighbors }];
    }),
  );
}

function contains(polygon: Vertex[], [px, py]: Vertex) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function frontWalls<T extends { id: number; points: Vertex[] }>(walls: T[]) {
  const base = [...walls]
    .sort((a, b) => a.id - b.id)
    .map((wall) => wall.points[0]);
  const winding = Math.sign(
    base.reduce((sum, [x, y], i) => {
      const [nextX, nextY] = base[(i + 1) % base.length];
      return sum + x * nextY - nextX * y;
    }, 0),
  );
  return walls.filter(
    (wall) => (wall.points[1][0] - wall.points[0][0]) * winding < 0,
  );
}

// Linear in the wall's facing and never above 94%, so a wall darkens at an even
// rate as it turns and the lit side still reads against a white page. (The
// configurator's brightness(1 + rightness * 0.18) clips light greys to white.)
function shade(rgb: number[], rightness: number) {
  const [r, g, b] = rgb.map((channel) =>
    Math.round(channel * (0.86 + rightness * 0.08)),
  );
  return `rgb(${r} ${g} ${b})`;
}
